import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import { ChatProvider, useChatContext } from '../bot-core/store/ChatContext';
import { BotExtProvider, useBotContext } from './store/BotContext';
import { useBotSession } from './hooks/useBotSession';
import { useChatSocket } from '../bot-core/hooks/useChatSocket';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import ChatButton from '../bot-core/ChatButton';
import ChatPanel from '../bot-core/ChatPanel';
import ChatHeader from '../bot-core/ChatHeader';
import ChatMessages from '../bot-core/ChatMessages';
import ChatInput from '../bot-core/ChatInput';
import ItinerarySummaryCard from './ItinerarySummaryCard';
import CheckoutPanel from './CheckoutPanel';
import CheckoutCtaButton from './CheckoutCtaButton';
import BotMessageExtras from './BotMessageExtras';
import BotSessionNotice from './BotSessionNotice';
import { useNewSessionCooldown } from './hooks/useNewSessionCooldown';
import { useSendCooldown } from '../bot-core/hooks/useSendCooldown';
import { formatCooldown } from './utils/botSessionLimit';
import { UIMessage } from '../bot-core/types/chat.types';
import { IoBagHandleOutline } from 'react-icons/io5';
import { Tabs, ConfigProvider } from 'antd';
import { botPopupContainer } from './utils/botPopupContainer';
import { useBotCheckoutHandler, extractBotSessionId } from './hooks/useBotCheckoutHandler';
import { handleBotTurnComplete } from './hooks/handleBotTurnComplete';
import { useWhatsappHandoff } from './hooks/useWhatsappHandoff';
import type {
  WhatsappAvailability,
  BotProductType,
} from './hooks/useBotAgentAvailability';
import { buildEmptyStateSubtitle } from './utils/buildEmptyStateSubtitle';
import { FaWhatsapp } from 'react-icons/fa';
import '../bot-core/chat.less';
import './bot.less';

interface BotWidgetInnerProps {
  whatsapp?: WhatsappAvailability | null;
  enabledProducts?: BotProductType[] | null;
}

const BotWidgetInner: React.FC<BotWidgetInnerProps> = ({
  whatsapp,
  enabledProducts,
}) => {
  const { state: chatState, dispatch: chatDispatch } = useChatContext();
  const { botState, botDispatch } = useBotContext();

  // useBotSession runs before useChatSocket (its state feeds onDone), so the
  // socket's cancelTurn is handed over through a ref once both have run.
  const cancelTurnRef = useRef<((sessionId: string) => void) | null>(null);
  const onAbandonSession = useCallback((sessionId: string) => {
    cancelTurnRef.current?.(sessionId);
  }, []);

  const {
    initSession,
    ensureSession,
    startNewChat,
    refreshSelections,
    fetchCheckout,
    tabs,
    activeTabId,
    switchTab,
    closeTab,
  } = useBotSession({ onAbandonSession });

  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const tenantId = Number(process.env.REACT_APP_TENANT_ID) || undefined;

  const onDone = useCallback(async (meta: any) => {
    if (!botState.sessionId) return;
    await handleBotTurnComplete(
      botState.sessionId,
      meta,
      refreshSelections,
      fetchCheckout,
      () => !!botState.checkoutPayload || !!botState.checkoutExpiresAt,
    );
  }, [
    botState.sessionId,
    botState.checkoutPayload,
    botState.checkoutExpiresAt,
    refreshSelections,
    fetchCheckout,
  ]);

  const onHandedOff = useCallback(
    (handedSessionId: string) => {
      if (handedSessionId === botState.sessionId) {
        chatDispatch({ type: 'SET_OPEN', isOpen: false });
        chatDispatch({
          type: 'ADD_MESSAGE',
          message: {
            id: crypto.randomUUID(),
            role: 'system',
            // TODO(i18n): translation key bot.handoff.handedOff
            text: 'This conversation continues on WhatsApp.',
            timestamp: new Date().toISOString(),
          } as UIMessage,
        });
      }
    },
    [botState.sessionId, chatDispatch],
  );

  // The BE auto-blocks a session mid-conversation (security guard / off-topic
  // strikes) and announces it on bot:blocked. Reuse the same sessionStatus the
  // widget already derives `isBlocked` from when a session LOADS as blocked.
  const onBlocked = useCallback(
    (blockedSessionId?: string) => {
      if (blockedSessionId && blockedSessionId !== botState.sessionId) return;
      botDispatch({ type: 'SET_SESSION_STATUS', status: 'blocked' });
    },
    [botState.sessionId, botDispatch],
  );

  // The session is finished (archived, or out of token budget). Same shape as
  // onBlocked, different status: `archived` is user-recoverable, so the notice
  // it drives offers "start a new chat" rather than telling them to contact
  // support.
  const onSessionEnded = useCallback(
    (endedSessionId?: string) => {
      if (endedSessionId && endedSessionId !== botState.sessionId) return;
      botDispatch({ type: 'SET_SESSION_STATUS', status: 'archived' });
    },
    [botState.sessionId, botDispatch],
  );

  const { connect, disconnect, sendMessage, bindSession, cancelTurn } = useChatSocket({
    namespace: '/bot-agent',
    onDone,
    onHandedOff,
    onBlocked,
    onSessionEnded,
    // bot-core owns no translations; hand it the localized line to use when a
    // turn dies with the connection.
    disconnectNotice: t?.connectionError,
  });
  cancelTurnRef.current = cancelTurn;

  // One socket serves every chat tab, so the socket has to be told which
  // session is on screen — anything streaming for a different one is dropped
  // instead of landing in this chat.
  useEffect(() => {
    bindSession(botState.sessionId ?? null);
  }, [botState.sessionId, bindSession]);

  const { handoff: triggerWhatsappHandoff, busy: handoffBusy } =
    useWhatsappHandoff();

  useEffect(() => { initSession(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Connect on open regardless of whether a session exists yet — the gateway
  // only authenticates on connect, it doesn't need a session. Connecting early
  // means the socket is ready by the time the first message is sent (which is
  // also what lazily creates the session), so the send_message emit isn't
  // racing the WebSocket handshake.
  useEffect(() => {
    if (chatState.isOpen) connect();
    else disconnect();
  }, [chatState.isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = () => chatDispatch({ type: 'SET_OPEN', isOpen: !chatState.isOpen });
  const handleClose = () => chatDispatch({ type: 'SET_OPEN', isOpen: false });

  const handleSend = (
    text: string,
    opts?: {
      selection?: {
        book_hash?: string;
        package_room?: {
          room_id?: string | number;
          original_room_id?: string;
          room_selection_key?: string;
          adult?: number;
          child?: number;
          infant?: number;
        };
        event_ticket?: {
          ticket_id?: string | number;
          quantity?: number;
          delivery_method_id?: number;
        };
        flight_pair?: {
          outbound_index: number;
          return_index: number;
        };
      };
    },
  ) => {
    // A new turn is starting — latch the previous turn's option lists as
    // superseded so they collapse once and never flicker back to interactive.
    // (Card selections reach here directly, bypassing ChatInput's dispatch.)
    chatDispatch({ type: 'MARK_LAST_SUPERSEDED' });
    // Lazily create the session on the first message so opening the widget
    // never persists an empty session. ensureSession returns the existing id
    // when one already exists, so subsequent sends just emit.
    void (async () => {
      const sessionId = botState.sessionId ?? (await ensureSession());
      if (!sessionId) return;
      sendMessage({
        session_id: sessionId,
        text,
        ...(tenantId ? { tenant_id: tenantId } : {}),
        // Structured room selection rides alongside the friendly text so the BE
        // can hand the LLM the exact book_hash (see BotAgentService selection hint).
        ...(opts?.selection ? { selection: opts.selection } : {}),
      });
    })();
  };

  const isBlocked = botState.sessionStatus === 'blocked';
  // Ended sessions 403 every further message, so the composer has to close for
  // the same reason a blocked one does — but the notice below offers a way out.
  const isEnded = botState.sessionStatus === 'archived';
  const hasActivity = chatState.messages.length > 0 || botState.selections.length > 0;

  // Per-IP new-session quota. It only blocks *creating* a session, so we gate
  // the composer solely while this chat has no session yet — any already-open
  // chat keeps working normally.
  const cooldown = useNewSessionCooldown();
  const cooldownBlocksSend = cooldown.active && !botState.sessionId;

  // Per-session message quota (bot:error code `rate_limited`). Different quota,
  // same UX as above: composer off, "please wait {{time}}" in the placeholder.
  const sendCooldown = useSendCooldown();
  const waitBlocksSend = cooldownBlocksSend || sendCooldown.active;
  const waitLabel = sendCooldown.active
    ? formatCooldown(sendCooldown.secondsLeft)
    : cooldown.label;
  const sessionNotice = (
    <BotSessionNotice
      t={t?.sessionLimit}
      cooldownActive={cooldownBlocksSend}
      cooldownLabel={cooldown.label}
      cooldownJustEnded={cooldown.justEnded && !botState.sessionId}
      genericError={botState.sessionCreateFailed}
      hasOtherChats={tabs.length > 0}
      sessionEnded={isEnded}
      onStartNewChat={startNewChat}
    />
  );

  const handleRemoveItem = (displayName: string) => {
    handleSend(`Remove ${displayName}`);
  };

  // Intercepts the bot-generated checkout link (`/shopping-cart?bot_session_id=…`).
  // Builds the V2 cart from BE, stashes to localStorage in the shape the
  // existing Checkout page expects, then navigates to `/checkout/<uuid>`.
  // Non-bot links fall through to the default react-router / `<a>` behaviour.
  const handleBotCheckout = useBotCheckoutHandler();
  const onLinkClick = useCallback(
    (url: string, _e: React.MouseEvent): boolean => {
      const sid = extractBotSessionId(url);
      if (!sid) return false;
      handleBotCheckout(sid);
      return true;
    },
    [handleBotCheckout],
  );

  const renderMessageExtra = (msg: UIMessage) => (
    <BotMessageExtras msg={msg} onSend={handleSend} />
  );

  const showCheckoutCta =
    !!botState.checkoutPayload &&
    !!botState.checkoutExpiresAt &&
    !chatState.isStreaming;
  const checkoutCtaBar = showCheckoutCta ? (
    <div className="bot-checkout-bar">
      <CheckoutCtaButton
        sessionId={botState.checkoutPayload!.session_id}
        expiresAt={botState.checkoutExpiresAt ?? undefined}
        onCheckout={handleBotCheckout}
        label={t?.checkout?.completeBooking}
      />
    </div>
  ) : null;

  const handleToggleCheckoutPanel = useCallback(async () => {
    if (botState.checkoutReady) {
      botDispatch({ type: 'HIDE_CHECKOUT' });
      return;
    }
    if (botState.sessionId && botState.checkoutPayload) {
      await fetchCheckout(botState.sessionId);
    }
    botDispatch({ type: 'SHOW_CHECKOUT' });
  }, [
    botState.checkoutReady,
    botState.sessionId,
    botState.checkoutPayload,
    botDispatch,
    fetchCheckout,
  ]);

  // "Preview cart" chip lives in the header for the ACTIVE tab — single
  // chip across the strip per the agreed UX. Toggles panel via SHOW/HIDE.
  // Always visible while there's a payload, regardless of panel state, so
  // it remains a discoverable indicator that the active tab has a checkout.
  const hasCheckoutPayload = !!botState.checkoutPayload;
  const showWhatsappHandoff =
    !!whatsapp?.enabled &&
    !!whatsapp?.business_number &&
    !!botState.sessionId;

  const headerActions = (
    <>
      {showWhatsappHandoff && (
        <button
          className="bot-icon-btn"
          // TODO(i18n): translation key bot.handoff.continueOnWhatsapp
          title="Continue on WhatsApp"
          disabled={handoffBusy}
          onClick={() => {
            if (botState.sessionId) triggerWhatsappHandoff(botState.sessionId);
          }}
        >
          <FaWhatsapp size={20} />
        </button>
      )}
      {hasCheckoutPayload && (
        <button
          className={`bot-icon-btn${botState.checkoutReady ? ' bot-icon-btn--active' : ''}`}
          title={t?.previewCart ?? 'Preview cart'}
          onClick={handleToggleCheckoutPanel}
        >
          <IoBagHandleOutline size={20} />
        </button>
      )}
    </>
  );

  // Whenever the active session shows its first user message, cache it as
  // the label so the tab strip keeps it after switching away (Redux-backed
  // `tabTitles` map — see BotContext.SET_TAB_TITLE).
  const firstUserText = useMemo(() => {
    const m = chatState.messages.find((m) => m.role === 'user');
    return m?.text;
  }, [chatState.messages]);
  useEffect(() => {
    if (activeTabId && firstUserText) {
      botDispatch({
        type: 'SET_TAB_TITLE',
        sessionId: activeTabId,
        title: firstUserText.slice(0, 40),
      });
    }
  }, [activeTabId, firstUserText, botDispatch]);

  // Browser-style tabs in the chat header. Label resolution order:
  //   1. botState.tabTitles[id] (persistent cache, survives switches)
  //   2. tab.title (snapshot at create time)
  //   3. localized "New chat" placeholder
  const newChatLabel = t?.newChat ?? 'New chat';
  const tabItems = tabs.map((tab) => ({
    key: tab.id,
    label:
      (botState.tabTitles[tab.id] && botState.tabTitles[tab.id].trim()) ||
      (tab.title && tab.title.trim()) ||
      newChatLabel,
    // The chat panel itself is rendered ONCE outside the Tabs container —
    // tab switching reloads the active session into that single panel.
    // Using `children` here just satisfies AntD's API.
    children: null,
    closable: true,
  }));

  const tabsStrip = tabs.length > 0 ? (
    <div className="bot-tabs-strip">
      <Tabs
        type="editable-card"
        activeKey={activeTabId ?? undefined}
        items={tabItems}
        hideAdd={false}
        onChange={(key) => switchTab(key)}
        onEdit={(targetKey, action) => {
          if (action === 'add') {
            startNewChat();
          } else if (action === 'remove' && typeof targetKey === 'string') {
            closeTab(targetKey);
          }
        }}
      />
    </div>
  ) : null;

  return (
    // getPopupContainer keeps antd overlays (Select, Tooltip, …) inside the
    // chat panel's stacking context — the panel sits at z-index 9999, so
    // body-portaled popups would otherwise render behind it (e.g. the ticket
    // quantity dropdown showing no options).
    <ConfigProvider getPopupContainer={botPopupContainer}>
      <ChatButton isOpen={chatState.isOpen} onClick={handleToggle} hasActivity={hasActivity} />
      {chatState.isOpen && (
        <ChatPanel
          header={
            <>
              <ChatHeader
                title={t?.title ?? 'Sky360 Travel Assistant'}
                avatarEmoji="✈️"
                extraActions={headerActions}
                onClose={handleClose}
              />
              {tabsStrip}
            </>
          }
          body={
            <ChatMessages
              avatarEmoji="✈️"
              isBlocked={isBlocked}
              emptyStateContent={
                <div className="bot-empty-state">
                  <div className="bot-empty-state__icon">✈️</div>
                  <div className="bot-empty-state__title">{t?.emptyState?.title ?? 'How can I help plan your trip?'}</div>
                  <div className="bot-empty-state__sub">{buildEmptyStateSubtitle(t?.emptyState, enabledProducts)}</div>
                </div>
              }
              blockedMessage={t?.blocked ?? 'This chat has been disabled.'}
              // extraBottom={botState.selections.length > 0 ? <ItinerarySummaryCard onRemove={handleRemoveItem} /> : null}
              renderMessageExtra={renderMessageExtra}
              onLinkClick={onLinkClick}
            />
          }
          footer={
            <>
              {checkoutCtaBar}
              {sessionNotice}
              <ChatInput
                onSend={handleSend}
                isBlocked={isBlocked || isEnded || waitBlocksSend}
                placeholder={t?.input?.placeholder ?? 'Ask me anything about your trip\u2026'}
                blockedPlaceholder={
                  !isBlocked && waitBlocksSend
                    ? (t?.sessionLimit?.inputPlaceholder ?? 'Please wait {{time}}\u2026').replace(
                        '{{time}}',
                        waitLabel,
                      )
                    : (t?.input?.disabled ?? 'Chat disabled')
                }
                starterReplies={t?.emptyState?.starters}
              />
            </>
          }
          overlay={botState.checkoutReady ? <CheckoutPanel /> : undefined}
        />
      )}
    </ConfigProvider>
  );
};

interface BotWidgetProps {
  whatsapp?: WhatsappAvailability | null;
  enabledProducts?: BotProductType[] | null;
}

const BotWidget: React.FC<BotWidgetProps> = ({ whatsapp, enabledProducts }) => (
  <ChatProvider>
    <BotExtProvider>
      <BotWidgetInner whatsapp={whatsapp} enabledProducts={enabledProducts} />
    </BotExtProvider>
  </ChatProvider>
);

export default BotWidget;
